import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n, pathFor } from "@/i18n/I18nProvider";

const CONSENT_KEY = "dinv_consent";

function updateConsent(prefs) {
  if (typeof window === "undefined") return;
  const gtag = window.gtag;
  if (typeof gtag === "function") {
    gtag("consent", "update", {
      ad_storage: prefs.marketing ? "granted" : "denied",
      ad_user_data: prefs.marketing ? "granted" : "denied",
      ad_personalization: prefs.marketing ? "granted" : "denied",
      analytics_storage: prefs.analytics ? "granted" : "denied",
      functionality_storage: prefs.functional ? "granted" : "denied",
      personalization_storage: prefs.functional ? "granted" : "denied",
      security_storage: "granted",
    });
  }
}

export default function CookieBanner() {
  const { lang, t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false, functional: true });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) setVisible(true);
      else {
        const parsed = JSON.parse(stored);
        updateConsent(parsed);
      }
    } catch (_) {
      setVisible(true);
    }
  }, []);

  const persist = (p) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...p, at: new Date().toISOString() }));
    } catch (_) {}
    updateConsent(p);
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () => persist({ analytics: true, marketing: true, functional: true });
  const rejectAll = () => persist({ analytics: false, marketing: false, functional: false });
  const savePrefs = () => persist(prefs);

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-slate-200 bg-white shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.25)]"
      role="dialog"
      aria-live="polite"
      aria-label="Cookies"
    >
      <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        {!showSettings ? (
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <h3 className="text-base font-semibold text-slate-900">{t("cookie.title")}</h3>
              <p className="mt-1 text-sm text-slate-600">
                {t("cookie.description")}{" "}
                <Link
                  to={pathFor(lang, "/cookies")}
                  className="font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                  data-testid="cookie-banner-policy-link"
                >
                  {t("cookie.policyLink")}
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-reject-button"
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                {t("cookie.reject")}
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                data-testid="cookie-settings-button"
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                {t("cookie.settings")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept-all-button"
                className="rounded-md bg-[#0A2540] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d2f52]"
              >
                {t("cookie.accept")}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-base font-semibold text-slate-900">{t("cookie.settingsTitle")}</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-700">{t("cookie.necessary")}</span>
                <input type="checkbox" checked readOnly className="h-4 w-4" data-testid="pref-necessary" />
              </label>
              <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
                <span className="text-sm text-slate-700">{t("cookie.functional")}</span>
                <input
                  type="checkbox"
                  checked={prefs.functional}
                  onChange={(e) => setPrefs({ ...prefs, functional: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="pref-functional"
                />
              </label>
              <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
                <span className="text-sm text-slate-700">{t("cookie.analytics")}</span>
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="pref-analytics"
                />
              </label>
              <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
                <span className="text-sm text-slate-700">{t("cookie.marketing")}</span>
                <input
                  type="checkbox"
                  checked={prefs.marketing}
                  onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="pref-marketing"
                />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                data-testid="cookie-settings-back"
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                {t("eligibility.back")}
              </button>
              <button
                type="button"
                onClick={savePrefs}
                data-testid="cookie-save-preferences"
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                {t("cookie.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
